import React, { useState, useRef, useEffect } from "react";

export default function UserDropdown({
  usernameOrEmail,
  userProfile,
  onLogout,
  onOpenSettings,
  onOpenMyProfile,
  showName: _showName = true
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const rawAppLabels = userProfile?.appsDisplay || userProfile?.apps_display || userProfile?.apps || [];
  const appLabels = [...new Set(rawAppLabels)];
  const rawRoleLabels = userProfile?.rolesDisplay ?? userProfile?.roles_display ?? (userProfile?.role ? [userProfile.role] : []);
  const roleLabels = [...new Set(Array.isArray(rawRoleLabels) ? rawRoleLabels : [rawRoleLabels].filter(Boolean))];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key and handle arrow key navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        // Return focus to the menu button
        const menuButton = dropdownRef.current?.querySelector('#menu-button');
        if (menuButton) {
          menuButton.focus();
        }
        return;
      }

      if (!isOpen) return;

      const menuItems = dropdownRef.current?.querySelectorAll('[role="menuitem"]');
      if (!menuItems) return;

      const currentIndex = Array.from(menuItems).findIndex(item => item === document.activeElement);

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
          menuItems[nextIndex].focus();
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
          menuItems[prevIndex].focus();
          break;
        }
        case 'Home':
          event.preventDefault();
          menuItems[0].focus();
          break;
        case 'End':
          event.preventDefault();
          menuItems[menuItems.length - 1].focus();
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  // Focus management for dropdown
  useEffect(() => {
    if (isOpen) {
      // Focus the first menu item when dropdown opens
      const firstMenuItem = dropdownRef.current?.querySelector('[role="menuitem"]');
      if (firstMenuItem) {
        firstMenuItem.focus();
      }
    }
  }, [isOpen]);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <div>
        {/* Mobile version - 3 vertical dots */}
        <button
          type="button"
          className="md:hidden inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          id="menu-button-mobile"
          aria-expanded={isOpen}
          aria-haspopup="true"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="User menu"
        >
          <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
        </button>

        {/* Desktop version - user icon only */}
        <button
          type="button"
          className="hidden md:inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          id="menu-button"
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label="User menu"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
            <span className="text-xs" aria-hidden="true">👤</span>
          </div>
          <svg className="-mr-1 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 010-1.06z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div
          className="absolute right-0 z-10 mt-2 w-64 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
          tabIndex="-1"
        >
          {/* User Info Section */}
          <div className="px-4 py-3" role="none">
            <p className="text-sm font-medium text-gray-900 truncate">
              {userProfile?.name || usernameOrEmail}
            </p>
            {userProfile?.email && userProfile.name !== userProfile.email && (
              <p className="text-xs text-gray-500 truncate">
                {userProfile.email}
              </p>
            )}
            {roleLabels.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Roles:</p>
                <div className="flex flex-wrap gap-1">
                  {roleLabels.slice(0, 3).map((role, index) => (
                    <span
                      key={`${role}-${index}`}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                    >
                      {role}
                    </span>
                  ))}
                  {roleLabels.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{roleLabels.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}
            {appLabels.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Apps:</p>
                <div className="flex flex-wrap gap-1">
                  {appLabels.slice(0, 3).map((app, index) => (
                    <span
                      key={`${app}-${index}`}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                    >
                      {app}
                    </span>
                  ))}
                  {appLabels.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{appLabels.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="py-1" role="none">
          {onOpenSettings && (
            <button
              onClick={() => {
                setIsOpen(false);
                onOpenSettings();
              }}
              className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left"
              role="menuitem"
              tabIndex="-1"
            >
              <svg className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
              </svg>
              Settings
            </button>
          )}
          {onOpenMyProfile && (
            <button
              onClick={() => {
                setIsOpen(false);
                onOpenMyProfile();
              }}
              className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left"
              role="menuitem"
              tabIndex="-1"
            >
              <svg className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              My profile
            </button>
          )}
            <button
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
              className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left"
              role="menuitem"
              tabIndex="-1"
            >
              <svg className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M3 3.5A1.5 1.5 0 014.5 2h4.879a1.5 1.5 0 011.06.44l3.122 3.12a1.5 1.5 0 01.44 1.06V12.5A1.5 1.5 0 0112.5 14h-1v-1h1a.5.5 0 00.5-.5v-7a.5.5 0 00-.146-.354l-3.122-3.121A.5.5 0 009.38 3H4.5a.5.5 0 00-.5.5v9a.5.5 0 00.5.5h7a.5.5 0 00.5-.5v-1h1v1a1.5 1.5 0 01-1.5 1.5H4.5A1.5 1.5 0 013 12.5v-9zM6 5a1 1 0 011-1h4a1 1 0 110 2H7a1 1 0 01-1-1zm0 3a1 1 0 011-1h4a1 1 0 110 2H7a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
